// src/utils/accountService.js
// Maneja el código de recuperación de cuenta (sin registro/contraseña):
// un secreto de alta entropía generado por el servidor que permite restaurar
// cursos comprados y lecturas guardadas en cualquier dispositivo.

const STORAGE_KEY = 'tarotAccountCode';

export const getLocalAccountCode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const setLocalAccountCode = (code) => {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // localStorage no disponible; el código sigue siendo válido en el servidor,
    // pero no se recordará en este navegador.
  }
};

// Devuelve el código existente, o crea uno nuevo en el servidor si todavía no hay.
// isNew=true indica que hay que mostrárselo al usuario para que lo guarde.
export const ensureAccountCode = async (userDetails) => {
  const existing = getLocalAccountCode();
  if (existing) return { code: existing, isNew: false };

  const response = await fetch('/api/account?action=create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: userDetails?.name, birthDate: userDetails?.birthDate }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'No se pudo crear tu código de cuenta.');

  setLocalAccountCode(data.code);
  return { code: data.code, isNew: true };
};

// Restaura cursos y lecturas asociados a un código en este dispositivo.
export const restoreAccount = async (code) => {
  const clean = (code || '').trim().toUpperCase();
  const response = await fetch(`/api/account?action=restore&code=${encodeURIComponent(clean)}`);
  const data = await response.json();
  if (!response.ok || !data.valid) throw new Error(data.error || 'Código no encontrado.');

  setLocalAccountCode(data.code);

  try {
    const purchasedCourses = JSON.parse(localStorage.getItem('purchasedCourses')) || {};
    data.courseIds.forEach((id) => { purchasedCourses[id] = true; });
    localStorage.setItem('purchasedCourses', JSON.stringify(purchasedCourses));

    const localReadings = JSON.parse(localStorage.getItem('tarotReadings')) || [];
    const byId = new Map(localReadings.map((r) => [r.id, r]));
    data.readings.forEach((r) => byId.set(r.id, r));
    const merged = Array.from(byId.values()).sort((a, b) => new Date(b.date) - new Date(a.date));
    localStorage.setItem('tarotReadings', JSON.stringify(merged));
  } catch (e) {
    console.error('Error fusionando datos restaurados:', e);
  }

  return data;
};

export const linkCourseToAccount = async (code, courseId) => {
  try {
    await fetch('/api/account?action=link-course', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, courseId }),
    });
  } catch (e) {
    console.error('Error vinculando curso a la cuenta:', e);
  }
};

export const syncReadingToAccount = async (code, reading) => {
  try {
    await fetch('/api/account?action=save-reading', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, reading }),
    });
  } catch (e) {
    console.error('Error guardando lectura en la cuenta:', e);
  }
};

export const deleteReadingFromAccount = async (code, readingId) => {
  if (!code) return;
  try {
    await fetch('/api/account?action=delete-reading', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, readingId }),
    });
  } catch (e) {
    console.error('Error borrando lectura de la cuenta:', e);
  }
};

// Guarda un email de contacto opcional junto al código de recuperación.
// OJO: esto NO dispara ningún flujo automático de recuperación por email —
// solo queda registrado para que soporte pueda verificar identidad manualmente.
export const setBackupEmail = async (code, email) => {
  const response = await fetch('/api/account?action=set-backup-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, email }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'No se pudo guardar el email de respaldo.');
  return data;
};
